<template>
  <div
    :id="`trail_${trail.trailIdLabel}`"
    class="c-trail_item cursor-pointer"
    @click.stop="cardClick"
    :class="{ active: trail.selected }"
  >
    <el-row :type="'flex'">
      <el-col class="item-name">
        <div class="cell">
          <i v-if="trail.riskScore > 0" class="color-block" :class="`${trail.riskLevel.level}-risk-color`"></i>
          <el-tooltip class="item" effect="dark" :content="`Trail ID`" placement="left" :enterable="false">
            <h6 class="display-inline-block">{{ trail.trailIdLabel }}</h6>
          </el-tooltip>
        </div>
      </el-col>
      <el-col class="item-name" v-if="!play">
        <el-tooltip class="item" effect="dark" :content="`Latest Detection Time`" placement="top" :enterable="false">
          <time class="body2">{{ $moment(trail.lastIocSeenTime).format(dateformat) }}</time>
        </el-tooltip>
      </el-col>
      <el-col class="item-name" v-if="trail.selected && play">
        <div class="cell box grow flex justify-flex-end">
          <span>
            <i
              class="material-icons item-expand"
              :class="{ disabled: currIndex === 0 }"
              @click.stop.prevent="currIndex !== 0 ? selectBack() : false"
              >chevron_left</i
            >
            <i
              class="material-icons item-expand"
              :class="{
                disabled: currIndex === trail.hostTimeInfoMap.length - 1
              }"
              @click.stop.prevent="selectNext()"
              >chevron_right</i
            >
          </span>
          <el-divider direction="vertical"></el-divider>
          <span @click.stop.prevent="closePlay($event)" class="close-sidebar cursor-pointer">
            <i class="far fa-times"></i>
          </span>
        </div>
      </el-col>
    </el-row>
    <el-row :type="'flex'">
      <el-col :span="8" style="margin: auto;">
        <div class="box grow flex column body2 item-score">
          <el-tooltip
            class="item"
            effect="dark"
            :content="`Risk Score`"
            placement="left"
            v-if="trail.riskScore > 0"
            :enterable="false"
          >
            <span class="box" v-if="trail.riskScore > 0">{{ trail.riskScore }}</span>
          </el-tooltip>
          <el-tooltip class="item" effect="dark" :content="`Dwell Time`" placement="left" :enterable="false">
            <span class="box">{{ trail.dwellTime }}</span>
          </el-tooltip>
        </div>
      </el-col>
      <el-col :span="16">
        <div class="cell flex item-summary body1">
          <div class="box grow flex column align-center">
            <el-tooltip class="item" effect="dark" :content="`Hosts Involved`" placement="top" :enterable="false">
              <span class="box icon icon-hosts"></span>
            </el-tooltip>
            <span class="box grow">{{ trail.numberOfHosts || 0 }}</span>
          </div>
          <div class="box grow flex column align-center">
            <el-tooltip class="item" effect="dark" :content="`Lateral Movements`" placement="top" :enterable="false">
              <span class="box icon icon-lat-movement"></span>
            </el-tooltip>
            <span class="box grow">{{ trail.numberOfLateralMovements || 0 }}</span>
          </div>
          <div class="box grow flex column align-center">
            <el-tooltip
              class="item"
              effect="dark"
              :content="trail.riskScore > 0 ? `Detections` : `Activities`"
              placement="top"
              :enterable="false"
            >
              <span class="box icon icon-detections"></span>
            </el-tooltip>
            <span class="box grow">{{ trail.numberOfDetections || 0 }}</span>
          </div>
          <div class="box grow flex column owner align-center" v-if="showOwner && !trail.selected && trail.ownerId">
            <el-tooltip
              class="item"
              effect="dark"
              :content="
                `${trail.ownerId.firstname ? trail.ownerId.firstname + ' ' + trail.ownerId.lastname : 'Not Assigned'}`
              "
              placement="bottom"
              :enterable="false"
            >
              <user-button :classList="trail.ownerId.firstname ? 'shamrock-200' : ''" :user="trail.ownerId" />
            </el-tooltip>
          </div>
          <div class="box grow flex column align-center" :class="{ 'display-none': !trail.selected }">
            <el-tooltip
              class="item"
              effect="dark"
              :content="`Play Threat Progression`"
              placement="bottom"
              :enterable="false"
            >
              <span class="box flex" @click.stop.prevent="playTrailProgression()">
                <i class="material-icons item-expand">play_circle_filled</i>
              </span>
            </el-tooltip>
          </div>
          <div class="box grow flex column align-center" :class="{ 'display-none': !trail.selected }">
            <el-tooltip
              class="item"
              effect="dark"
              :content="`Threat Progression Details`"
              placement="bottom"
              :enterable="false"
            >
              <span class="box flex link" @click="openLink()"> <i class="icon icon-trail" /> </span>
            </el-tooltip>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>
<script>
import { DATETIME_FORMAT } from "@/constants/datetime.constants";
import UserButton from "@/components/button/UserButton.vue";

export default {
  name: "TrailInfoCard",
  props: {
    trail: {
      type: Object,
      default: () => {
        return {};
      }
    },
    showOwner: { type: Boolean, default: true },
    parent: { type: String }
  },
  data() {
    return {
      currIndex: 0,
      play: false,
      dateformat: DATETIME_FORMAT,
      cardClick: {}
    };
  },
  methods: {
    playTrailProgression() {
      this.play = !this.play;
      if (this.play) {
        this.selectFirst();
      } else {
        this.currIndex = 0;
      }
    },
    openLink() {
      this.$router.push({
        name: "attack-track-details",
        params: { trailId: this.trail.id }
      });
    },
    selectFirst() {
      this.$emit("select-next", {
        trail: this.trail,
        hostIndex: 0
      });
    },
    selectBack() {
      if (this.currIndex === 0) {
        return false;
      }

      this.$emit("select-previous", {
        trail: this.trail,
        hostIndex: --this.currIndex
      });
    },
    selectNext() {
      if (this.currIndex === this.trail.hostTimeInfoMap.length - 1) {
        return false;
      }

      this.$emit("select-next", {
        trail: this.trail,
        hostIndex: ++this.currIndex
      });
    },
    closePlay(ev) {
      this.play = false;
      this.currIndex = 0;

      this.$emit("select-close", ev);
    }
  },
  created() {
    this.cardClick = _.debounce(event => {
      if (_.isUndefined(this.trail.selected)) {
        this.$set(this.trail, "selected", true);
      } else {
        this.$set(this.trail, "selected", !this.trail.selected);
      }
      this.$emit("card-click", event);
    }, 250);
  },
  watch: {
    "trail.selected": {
      handler: function(newVal, oldVal) {
        if (newVal === false) {
          this.play = false;
          this.currIndex = 0;
        }
      },
      immediate: true,
      deep: true
    }
  },
  beforeDestroy: function() {},
  components: { UserButton }
};
</script>

<style scoped>
@import "assets/styles/_variables.css";
@import "assets/styles/colors.css";
@import "assets/styles/text.css";
@import "assets/styles/icon.css";

.c-trail_item {
  padding: 8px 8px;

  .cell {
    box-sizing: border-box;
    white-space: normal;
    word-break: break-all;
  }

  .item-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    h6 {
      line-height: 20px;
    }

    .el-divider--vertical {
      height: 1.25em;
    }

    .material-icons {
      font-size: 1.25em;
      color: var(--aluminum-600);
    }

    time {
      color: var(--gray-400);
      float: right;
    }

    .timestamp {
      vertical-align: text-top;
    }
  }

  .item-score {
    margin-left: 12%;

    span {
      display: block;
    }
  }

  .close-sidebar {
    font-size: 1.25em;
    line-height: 1em;
  }

  .item-summary {
    .summary-block {
      height: 16px;
      width: 16px;
      border-radius: 50%;
      margin: auto;
      margin-bottom: 4px;
    }

    span {
      text-align: center;

      &.icon-hosts {
        margin: 0px;
      }
    }
  }

  .material-icons {
    font-size: 2em;
    margin-left: 10px;
  }

  .item-expand.disabled {
    color: var(--tuna-400);
  }

  .circle {
    width: 20px;
    height: 20px;
    display: table-cell;
    border-radius: 50%;
    font-size: 10px;
    color: #fff;
    line-height: 20px;
    text-align: center;
    background: var(--primary-blue-400);
  }

  .avatar-wrapper {
    align-items: stretch;
  }

  .avatar-circle {
    color: white;
    letter-spacing: -1px;
    font-size: 13px;
    height: 32px;
    width: 32px;
    padding: 0px;
    margin: 0px;
    position: relative;
  }

  .icon-unassigned {
    width: 32px;
    height: 32px;
    margin: 0px;
    mask-size: cover;
  }

  .link {
    padding: 1px;
    margin-top: 5px;

    .icon {
      mask-size: cover;
      color: var(--aluminum-600);
    }
  }

  .material-icons.item-expand,
  .icon-trail {
    color: var(--aluminum-600);
  }
}

.c-trail_item.active {
  box-shadow: 2px 2px 12px 2px rgba(0, 0, 0, 0.1);
}

.c-trail_item:hover {
  background-color: var(--aluminum-100);
}

.active-row {
  box-shadow: 2px 2px 12px 2px rgba(0, 0, 0, 0.1);
}
</style>



// WEBPACK FOOTER //
// src/components/card/TrailInfoCard.vue